import { CheckCircle } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { getUserTypeFromToken } from '../../_helpers/cookie'
import { useAppDispatch, useAppSelector } from '../../_helpers/hooks'
import { handleMessages } from '../../_helpers/utilities'
import { IBusinessProfile, ISuperAdminBusiness } from '../../_models/superAdmin/businessInterface'
import { publicPagesState } from '../../slices/publicPagesSlice'
import { errorToast } from '../../slices/toastSlice'
import { userState } from '../../slices/userSlice'
interface IBusinessProfiles {
  profiles?: IBusinessProfile[]
  switchProfile: (profileType: string, profileData?: IBusinessProfile | { id: number }) => void
  superAdmin?: boolean
  superAdminBusinesses?: ISuperAdminBusiness[]
}

const ListBusinessProfiles: FC<IBusinessProfiles> = ({
  profiles,
  switchProfile,
  superAdmin,
  superAdminBusinesses,
}) => {
  const theme = useTheme()
  const userType = getUserTypeFromToken()
  const userDetails = useAppSelector(userState)
  // eslint-disable-next-line camelcase
  const { userProfileInformation, active_business_id } = userDetails
  const { isSubscriptionBased } = useAppSelector(publicPagesState)
  const { t } = useTranslation()
  // eslint-disable-next-line camelcase
  const activeBusinessId = active_business_id
  const dispatch = useAppDispatch()
  return (
    <>
      {!superAdmin &&
        profiles?.map((profile, index) => {
          const isFirstProfile = index === 0
          return (
            <List key={profile.id}>
              {!isFirstProfile && <Divider sx={{ mb: 1, ml: 2, mr: 2 }} />}
              <ListItem
                disablePadding
                sx={{ '&:hover .show-on-hover': { display: { sm: 'block' } } }}
              >
                <ListItemButton
                  onClick={() => {
                    // eslint-disable-next-line camelcase
                    if (activeBusinessId !== profile.id) {
                      if (
                        profile.hasActivePlan ||
                        (!profile.hasActivePlan && profile.hasSubscriptionAddPermission)
                      ) {
                        switchProfile('business', profile)
                      } else if (!profile.hasActivePlan && !profile.hasSubscriptionAddPermission) {
                        dispatch(
                          errorToast(
                            handleMessages(t('superAdminProfileDrawer.contactBusinessAdminText')),
                          ),
                        )
                      }
                    }
                  }}
                >
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    sx={{ width: '100%' }}
                    alignItems='center'
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      sx={{
                        overflow: 'hidden',
                      }}
                    >
                      <Avatar src={profile.cover} alt={profile.name} />
                      <Stack direction='column' ml={2}>
                        <Tooltip title={profile?.name} placement='bottom-start'>
                          <Typography
                            lineHeight={1}
                            variant='subtitle1'
                            color='common.black'
                            fontWeight={'normal'}
                            dir='ltr'
                            sx={{
                              [theme.breakpoints.up('md')]: {
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                maxWidth: '10rem',
                              },
                            }}
                          >
                            {profile?.name}
                          </Typography>
                        </Tooltip>
                        {isSubscriptionBased && !profile.hasActivePlan && (
                          <Box
                            sx={{
                              backgroundColor: theme.palette.publicPages.colorOneHover,
                              borderRadius: '0.5rem',
                              px: [1],
                              mt: 0.5,
                            }}
                          >
                            <Typography color='white' variant='subtitle2' textAlign={'center'}>
                              {t('superAdminProfileDrawer.subscriptionExpired')}
                            </Typography>
                          </Box>
                        )}
                        <Tooltip title={profile?.role} placement='bottom-start'>
                          <Typography
                            variant='subtitle2'
                            color='description.light'
                            fontWeight={300}
                            dir='ltr'
                            sx={{
                              [theme.breakpoints.up('md')]: {
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                maxWidth: '10rem',
                              },
                            }}
                          >
                            {profile?.role}
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </Box>
                    <Box display='flex' justifyContent='flex-end'>
                      <Box>
                        {
                          // eslint-disable-next-line camelcase
                          activeBusinessId !== profile.id ? (
                            <Box ml={4} className='show-on-hover' display='none'>
                              <Typography variant='subtitle1' color='primary.main' fontWeight={600}>
                                {t('superAdminProfileDrawer.switchText')}
                              </Typography>
                            </Box>
                          ) : (
                            <CheckCircle color='success' />
                          )
                        }
                      </Box>
                    </Box>
                  </Stack>
                </ListItemButton>
              </ListItem>
            </List>
          )
        })}

      {superAdmin &&
        superAdminBusinesses?.map((profile: ISuperAdminBusiness, index) => {
          const isLastProfile = index === superAdminBusinesses.length - 1
          return (
            <List key={profile.id}>
              <ListItem
                disablePadding
                sx={{
                  '&:hover .show-on-hover': {
                    display: { sm: 'block', marginTop: 0, paddingTop: 0 },
                  },
                }}
              >
                <ListItemButton
                  onClick={() => {
                    if (activeBusinessId !== profile.id) {
                      switchProfile('business', { id: profile.id })
                    }
                  }}
                >
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    sx={{ width: '100%', marginTop: 0, paddingTop: 0 }}
                    alignItems='center'
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      sx={{
                        overflow: 'hidden',
                        paddingBottom: '0.2rem',
                        marginTop: 0,
                        paddingTop: 0,
                      }}
                    >
                      <Avatar src={profile.cover} alt={profile.name} sx={{ borderRadius: '8px' }} />
                      <Stack direction='column' ml={2}>
                        <Stack direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
                          <Tooltip title={profile?.name} placement='bottom-start'>
                            <Typography
                              lineHeight={1}
                              variant='subtitle1'
                              color='common.black'
                              fontWeight='normal'
                              dir='ltr'
                              sx={{
                                [theme.breakpoints.up('md')]: {
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  textAlign: 'left',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '10rem',
                                },
                              }}
                            >
                              {profile?.name}
                            </Typography>
                          </Tooltip>
                          {profile.status === 0 ? (
                            <Chip
                              label={t('superAdminProfileDrawer.inactiveLabel')}
                              size='small'
                              sx={{
                                backgroundColor: theme.palette.profileIcon.colorEighteen,
                                color: theme.palette.profileIcon.colorNineteen,
                                borderRadius: '0.3rem',
                                height: '1.25rem',
                                '& .MuiChip-label': {
                                  fontSize: '0.75rem',
                                  padding: 0.5,
                                },
                              }}
                            />
                          ) : (
                            ''
                          )}
                        </Stack>
                        <Typography
                          variant='subtitle2'
                          color='description.light'
                          fontWeight={300}
                          dir='ltr'
                          sx={{
                            [theme.breakpoints.up('md')]: {
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              textAlign: 'left',
                              whiteSpace: 'nowrap',
                              maxWidth: '10rem',
                            },
                          }}
                        >
                          {profile?.facility_count === 1
                            ? t('superAdminProfileDrawer.facilityLabel', {
                                count: profile?.facility_count,
                              })
                            : t('superAdminProfileDrawer.facilitiesLabel', {
                                count: profile?.facility_count,
                              })}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box display='flex' justifyContent='flex-end'>
                      <Box>
                        {
                          // eslint-disable-next-line camelcase
                          activeBusinessId !== profile.id ? (
                            <Box ml={4} className='show-on-hover' display='none'>
                              <Typography variant='subtitle1' color='primary.main' fontWeight={600}>
                                {t('profileDrawer.switchText')}
                              </Typography>
                            </Box>
                          ) : (
                            <CheckCircle color='success' />
                          )
                        }
                      </Box>
                    </Box>
                  </Stack>
                </ListItemButton>
              </ListItem>
              {!isLastProfile && <Divider sx={{ ml: 2, mr: 2 }} />}
            </List>
          )
        })}

      {!superAdmin && (
        <ListItem disablePadding sx={{ '&:hover .show-on-hover': { display: { sm: 'block' } } }}>
          <ListItemButton onClick={() => switchProfile('customer')}>
            <Stack
              direction='row'
              justifyContent='space-between'
              sx={{ width: '100%' }}
              alignItems='center'
            >
              <Box display='flex' alignItems='center'>
                <Avatar
                  src={userProfileInformation.image}
                  alt={userProfileInformation.customer_name}
                />
                <Stack direction='column' ml={2}>
                  <Typography
                    lineHeight={1}
                    variant='subtitle1'
                    color='common.black'
                    fontWeight={'normal'}
                  >
                    {t('superAdminProfileDrawer.personalText')}
                  </Typography>
                </Stack>
              </Box>
              <Box display='flex' justifyContent='flex-end'>
                <Box>
                  {
                    <Box>
                      {userType === 'customer' ? (
                        <CheckCircle color='success' />
                      ) : (
                        <Box ml={4} className='show-on-hover' display='none'>
                          <Typography variant='subtitle1' color='primary.main' fontWeight={600}>
                            {t('superAdminProfileDrawer.switchText')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                </Box>
              </Box>
            </Stack>
          </ListItemButton>
        </ListItem>
      )}
    </>
  )
}

export default ListBusinessProfiles
